// src/components/Atelier 2/Activite1/ThreatCategories.js
import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Check, Edit, Trash2, Plus, Eye, EyeOff, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useAnalysisStore from '../../../stores/useAnalysisStore';
import useAtelier2Store from '../../../stores/useAtelier2Store';
import CategoryEditorModal from './CategoryEditorModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { showSuccessToast, showErrorToast } from '../../../utils/toastUtils';

// Built-in category IDs — MUST stay stable forever. Never rename.
// Sources de risque store these IDs as `category` field; renaming would orphan every source.
export const BUILT_IN_CATEGORY_IDS = [
  'etatique',
  'criminel',
  'terroriste',
  'activiste',
  'officine',
  'amateur',
  'vengeur',
  'malveillant'
];

// ponytail: visuals (icon + Tailwind color class) live here, not in DB. Backend only owns name/description.
const BUILT_IN_VISUALS = {
  etatique:    { color: 'bg-red-600 border-red-700 text-red-50',       bgLight: 'bg-red-100',       text: 'text-red-700',       border: 'border-red-500',       ring: 'ring-red-300',       icon: '🏛️' },
  criminel:    { color: 'bg-purple-600 border-purple-700 text-purple-50', bgLight: 'bg-purple-100',   text: 'text-purple-700',   border: 'border-purple-500',   ring: 'ring-purple-300',   icon: '🔪' },
  terroriste:  { color: 'bg-orange-600 border-orange-700 text-orange-50', bgLight: 'bg-orange-100',   text: 'text-orange-700',   border: 'border-amber-500',   ring: 'ring-amber-300',    icon: '💣' },
  activiste:   { color: 'bg-green-600 border-green-700 text-green-50',  bgLight: 'bg-green-100',     text: 'text-green-700',    border: 'border-green-500',    ring: 'ring-green-300',    icon: '✊' },
  officine:    { color: 'bg-blue-600 border-blue-700 text-blue-50',     bgLight: 'bg-blue-100',      text: 'text-blue-700',     border: 'border-blue-500',     ring: 'ring-blue-300',     icon: '🕵️' },
  amateur:     { color: 'bg-yellow-600 border-yellow-700 text-yellow-50', bgLight: 'bg-yellow-100', text: 'text-yellow-700',   border: 'border-yellow-500',   ring: 'ring-yellow-300',   icon: '🎮' },
  vengeur:     { color: 'bg-pink-600 border-pink-700 text-pink-50',     bgLight: 'bg-pink-100',      text: 'text-pink-700',     border: 'border-pink-500',     ring: 'ring-pink-300',     icon: '😡' },
  malveillant: { color: 'bg-indigo-600 border-indigo-700 text-indigo-50', bgLight: 'bg-indigo-100',  text: 'text-indigo-700',   border: 'border-indigo-500',   ring: 'ring-indigo-300',   icon: '🦹' }
};

// ponytail: same swatches reused by the editor modal. Keep in sync with BUILT_IN_VISUALS above.
export const CUSTOM_COLOR_SWATCHES = [
  { name: 'red',    hex: '#DC2626' },
  { name: 'purple', hex: '#9333EA' },
  { name: 'orange', hex: '#EA580C' },
  { name: 'amber',  hex: '#F59E0B' },
  { name: 'green',  hex: '#16A34A' },
  { name: 'blue',   hex: '#2563EB' },
  { name: 'yellow', hex: '#CA8A04' },
  { name: 'pink',   hex: '#DB2777' },
  { name: 'indigo', hex: '#4F46E5' },
  { name: 'teal',   hex: '#0D9488' },
  { name: 'slate',  hex: '#475569' },
  { name: 'gray',   hex: '#6B7280' }
];

const getBuiltInVisuals = (id) => BUILT_IN_VISUALS[id] || { color: 'bg-gray-600 border-gray-700 text-gray-50', bgLight: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-500', ring: 'ring-gray-300', icon: '🛡️' };

// ponytail: backward-compat. Older callers import THREAT_CATEGORIES and look up by id/name.
// We re-export the built-in visuals list (same shape as before) so existing imports still resolve.
// Custom categories are NOT in this list; callers that need them must look them up separately
// (useAtelier2Store.customCategories + resolveCategory).
export const THREAT_CATEGORIES = BUILT_IN_CATEGORY_IDS.map(id => {
  const v = BUILT_IN_VISUALS[id];
  return {
    id,
    color: v.color,
    icon: v.icon
    // name/description are added by getThreatCategories() via i18n — keep parity with old export
  };
});

// Same as old behavior — built-ins with i18n-applied names/descriptions/details. Callers that
// only need built-ins (e.g., CategorySourcesTable) can keep using this.
export const getThreatCategories = (t) => {
  return BUILT_IN_CATEGORY_IDS.map(id => {
    const v = BUILT_IN_VISUALS[id];
    return {
      id,
      name: t(`workshop2.activity1.categories.${id}.name`),
      description: t(`workshop2.activity1.categories.${id}.description`),
      details: t(`workshop2.activity1.categories.${id}.details`),
      color: v.color,
      icon: v.icon
    };
  });
};

// Helper for callers that have neither i18n nor built-in visuals at hand — returns a unified
// { id, name } from either built-ins (i18n required) or a customCategories list.
export const resolveCategoryName = (id, customCategories, t) => {
  if (BUILT_IN_CATEGORY_IDS.includes(id)) {
    return {
      id,
      name: t(`workshop2.activity1.categories.${id}.name`)
    };
  }
  const c = (customCategories || []).find(x => x.id === id);
  return c ? { id, name: c.name } : { id, name: id };
};

// Resolves any category (built-in or custom) to a unified shape for rendering.
const resolveCategory = (cat, t) => {
  const isBuiltIn = cat.isBuiltIn === true || BUILT_IN_CATEGORY_IDS.includes(cat.id);
  if (isBuiltIn) {
    const visuals = getBuiltInVisuals(cat.id);
    return {
      id: cat.id,
      isBuiltIn: true,
      name: t(`workshop2.activity1.categories.${cat.id}.name`),
      description: t(`workshop2.activity1.categories.${cat.id}.description`),
      details: t(`workshop2.activity1.categories.${cat.id}.details`),
      icon: visuals.icon,
      color: visuals.color,
      bgLight: visuals.bgLight,
      text: visuals.text,
      border: visuals.border,
      ring: visuals.ring
    };
  }
  // Custom: pick a swatch hex for border/bgLight as best we can
  const swatch = CUSTOM_COLOR_SWATCHES.find(s => s.hex === cat.color) || CUSTOM_COLOR_SWATCHES[0];
  return {
    id: cat.id,
    isBuiltIn: false,
    name: cat.name,
    description: cat.description || '',
    details: '',
    icon: cat.icon || '🛡️',
    color: '',
    bgLight: `bg-[${swatch.hex}]/10`,
    text: `text-[${swatch.hex}]`,
    border: `border-[${swatch.hex}]`,
    ring: `ring-[${swatch.hex}]`,
    raw: cat
  };
};

const ThreatCategories = ({ externalSelectedCategories, setExternalSelectedCategories }) => {
  const { t } = useTranslation();
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [hoverTimer, setHoverTimer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState(null);

  const analysisStore = useAnalysisStore();
  const atelier2Store = useAtelier2Store();
  const currentAnalysis = analysisStore.currentAnalysis;
  const analysisId = currentAnalysis?.id;

  // Store-backed state (shared with SourcesRisques / CategorySourcesTable)
  const customCategories = useAtelier2Store((s) => s.customCategories);
  const storeSelected = useAtelier2Store((s) => s.threatCategoriesSelected);
  const hiddenCategories = useAtelier2Store((s) => s.hiddenCategories);
  const sourcesRisque = useAtelier2Store((s) => s.sourcesRisque);

  const actualSelectedCategories = externalSelectedCategories || storeSelected;
  const actualSetSelectedCategories = setExternalSelectedCategories || atelier2Store.setThreatCategoriesSelected;

  // Load categories + selection into the store on mount/analysis change.
  // ponytail: parent (Activite1) already triggers loadData; we still defensively load here
  // for the case where ThreatCategories is mounted standalone.
  useEffect(() => {
    if (!analysisId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        await atelier2Store.loadThreatCategories(analysisId);
      } catch (error) {
        console.error('Error loading threat categories:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisId]);

  // Build the full merged list: 8 built-ins + custom (pre-filter, used by the config panel)
  const fullList = useMemo(() => {
    const builtIns = BUILT_IN_CATEGORY_IDS.map(id => resolveCategory({ id, isBuiltIn: true }, t));
    const customs = customCategories.map(c => resolveCategory({ ...c, isBuiltIn: false }, t));
    return [...builtIns, ...customs];
  }, [customCategories, t]);

  // ponytail: visible list is what the grid renders. Hidden IDs are excluded. Nothing else cares.
  const visibleList = useMemo(
    () => fullList.filter(c => !hiddenCategories.includes(c.id)),
    [fullList, hiddenCategories]
  );

  // Persist current state to backend
  const persist = async (nextSelected, nextCustom, nextHidden) => {
    if (!analysisId) return false;
    setSaving(true);
    try {
      const response = await atelier2Store.saveThreatCategories(analysisId, {
        selected: nextSelected,
        customCategories: nextCustom,
        hidden: nextHidden !== undefined ? nextHidden : hiddenCategories
      });
      if (response?.success) return true;
      throw new Error(response?.message || 'Erreur de sauvegarde');
    } catch (error) {
      console.error('Error saving categories:', error);
      showErrorToast(error.message || 'Erreur lors de la sauvegarde');
      // ponytail: revert optimistic update by reloading from server
      await atelier2Store.loadThreatCategories(analysisId);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Toggle selection of a category
  const toggleCategorySelection = (categoryId) => {
    const next = actualSelectedCategories.includes(categoryId)
      ? actualSelectedCategories.filter(id => id !== categoryId)
      : [...actualSelectedCategories, categoryId];
    actualSetSelectedCategories(next);
    persist(next, customCategories);
  };

  // ponytail: toggle a category's "hidden" flag. Locks when the category has sources.
  const toggleHidden = (categoryId) => {
    const sourceCount = sourcesRisque.filter(s => s.category === categoryId).length;
    if (sourceCount > 0) {
      showErrorToast(
        t('workshop2.activity1.categoryConfig.lockedTooltip', { count: sourceCount })
      );
      return;
    }
    const nextHidden = hiddenCategories.includes(categoryId)
      ? hiddenCategories.filter(id => id !== categoryId)
      : [...hiddenCategories, categoryId];
    atelier2Store.setHiddenCategories(nextHidden);
    persist(actualSelectedCategories, customCategories, nextHidden);
  };

  // ponytail: one-shot reset. No memoization needed — button is rarely clicked.
  const showAllCategories = () => {
    atelier2Store.setHiddenCategories([]);
    persist(actualSelectedCategories, customCategories, []);
  };

  // Open editor for new or existing custom category
  const openEditor = (category = null) => {
    setEditingCategory(category);
    setEditorOpen(true);
  };

  // Save custom category from modal
  const handleSaveCustom = async (draft) => {
    let nextCustom;
    let nextSelected = actualSelectedCategories;
    if (editingCategory) {
      nextCustom = customCategories.map(c => c.id === editingCategory.id ? { ...c, ...draft, id: c.id } : c);
    } else {
      const newId = `cat-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
      nextCustom = [...customCategories, { id: newId, ...draft }];
      if (!actualSelectedCategories.includes(newId)) {
        nextSelected = [...actualSelectedCategories, newId];
        actualSetSelectedCategories(nextSelected);
      }
    }
    atelier2Store.setCustomCategories(nextCustom);
    // ponytail: await persist BEFORE closing the modal — a reload mid-flight can't lose the save anymore.
    const ok = await persist(nextSelected, nextCustom);
    if (ok) {
      showSuccessToast(editingCategory ? t('workshop2.activity1.categoryManager.toast.updated') : t('workshop2.activity1.categoryManager.toast.created'));
    }
    setEditorOpen(false);
    setEditingCategory(null);
  };

  // Open delete confirmation for custom category
  const openDelete = (category) => {
    setDeletingCategory(category);
    setDeleteOpen(true);
  };

  // Compute affected sources for the category being deleted
  const affectedSources = useMemo(() => {
    if (!deletingCategory) return [];
    return sourcesRisque.filter(s => s.category === deletingCategory.id);
  }, [deletingCategory, sourcesRisque]);

  // Confirm delete: cascade
  const handleConfirmDelete = async () => {
    if (!deletingCategory) return;
    const removedId = deletingCategory.id;
    const nextCustom = customCategories.filter(c => c.id !== removedId);
    const nextSelected = actualSelectedCategories.filter(id => id !== removedId);
    atelier2Store.setCustomCategories(nextCustom);
    actualSetSelectedCategories(nextSelected);
    setDeleteOpen(false);
    setDeletingCategory(null);
    const ok = await persist(nextSelected, nextCustom);
    if (ok) {
      showSuccessToast(t('workshop2.activity1.categoryManager.toast.deleted', { count: affectedSources.length }));
    }
  };

  // Hover handlers — untouched
  const handleHoverStart = (categoryId) => {
    if (hoverTimer) clearTimeout(hoverTimer);
    const timer = setTimeout(() => setExpandedCategory(categoryId), 1000);
    setHoverTimer(timer);
  };
  const handleHoverEnd = () => {
    if (hoverTimer) {
      clearTimeout(hoverTimer);
      setHoverTimer(null);
    }
    setExpandedCategory(null);
  };
  useEffect(() => () => { if (hoverTimer) clearTimeout(hoverTimer); }, [hoverTimer]);

  // ponytail: a single render path now. Built-ins render with built-in visuals, customs with their own.
  const renderCard = (category) => {
    const isSelected = actualSelectedCategories.includes(category.id);
    const isExpanded = expandedCategory === category.id;
    const visuals = category.isBuiltIn
      ? { bgLight: category.bgLight, text: category.text, border: category.border, ring: category.ring }
      : { bgLight: '', text: '', border: category.border, ring: category.ring };

    return (
      <motion.div
        key={category.id}
        whileHover={{ y: -5 }}
        onHoverStart={() => handleHoverStart(category.id)}
        onHoverEnd={handleHoverEnd}
        className={`rounded-xl overflow-hidden transition-all duration-300 cursor-pointer relative ${isSelected ?
          `border-4 ${category.border} ring-2 ${category.ring} ring-offset-1`
          : 'border border-gray-200'}`}
        style={{
          boxShadow: isSelected ? '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        }}
        onClick={(e) => {
          e.preventDefault();
          toggleCategorySelection(category.id);
        }}
      >
        <div className="bg-white h-full">
          <div className="flex justify-between items-start p-5">
            <div className="flex items-center">
              <div
                className={`text-3xl p-2 rounded-full mr-3 ${isSelected ? visuals.bgLight : 'bg-gray-100'}`}
                style={!category.isBuiltIn ? {
                  backgroundColor: isSelected ? `${category.raw.color}20` : '#f3f4f6',
                  border: `1px solid ${category.raw.color}40`,
                  color: category.raw.color
                } : undefined}
              >
                {category.icon}
              </div>
              <div>
                <h3
                  className={`font-bold text-lg ${isSelected ? visuals.text : 'text-gray-800'}`}
                  style={!category.isBuiltIn && isSelected ? { color: category.raw.color } : undefined}
                >
                  {category.name}
                </h3>
                <p className={`text-sm ${isSelected ? 'text-gray-700' : 'text-gray-600'}`}>
                  {category.description}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  toggleCategorySelection(category.id);
                }}
                onMouseEnter={(e) => e.stopPropagation()}
                onMouseLeave={(e) => e.stopPropagation()}
                className={`p-2 rounded-full transition-colors duration-200 ${isSelected ? `${visuals.bgLight} ${visuals.text}` : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                style={!category.isBuiltIn && isSelected ? { backgroundColor: `${category.raw.color}20`, color: category.raw.color } : undefined}
                aria-label={isSelected ? t('workshop2.activity1.threatCategories.deselectButton') : t('workshop2.activity1.threatCategories.selectButton')}
              >
                <Check size={20} />
              </button>
              {!category.isBuiltIn && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      openEditor(category.raw);
                    }}
                    onMouseEnter={(e) => e.stopPropagation()}
                    className="p-2 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200"
                    aria-label={t('workshop2.activity1.categoryManager.edit')}
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      openDelete(category.raw);
                    }}
                    onMouseEnter={(e) => e.stopPropagation()}
                    className="p-2 rounded-full bg-red-100 text-red-600 hover:bg-red-200"
                    aria-label={t('workshop2.activity1.categoryManager.delete')}
                  >
                    <Trash2 size={16} />
                  </button>
                </>
              )}
            </div>
          </div>

          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="px-5 pb-5 text-gray-600"
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
              onMouseEnter={(e) => e.stopPropagation()}
            >
              <div className="pt-4 border-t border-gray-200">
                <p>{category.details || category.description}</p>
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>
    );
  };

  if (loading) {
    return <div className="text-center text-gray-500 py-8">{t('common.loading', 'Chargement...')}</div>;
  }

  return (
    <div className="space-y-6">
      {/* ponytail: native <details> handles open/close for free. No state, no portal, no animation lib. */}
      <details className="bg-gray-50 border border-gray-200 rounded-lg group">
        <summary className="cursor-pointer px-4 py-3 flex items-center justify-between list-none">
          <span className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Settings size={16} />
            {t('workshop2.activity1.categoryConfig.title', 'Configurer les catégories')}
            {hiddenCategories.length > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-indigo-100 text-indigo-700">
                {hiddenCategories.length} masquée(s)
              </span>
            )}
          </span>
          <span className="flex items-center gap-2">
            {hiddenCategories.length > 0 && (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); showAllCategories(); }}
                className="text-xs px-2 py-1 rounded text-indigo-600 hover:bg-indigo-50"
              >
                {t('workshop2.activity1.categoryConfig.showAll', 'Tout afficher')}
              </button>
            )}
            <span className="text-xs text-gray-400 group-open:rotate-180 transition-transform">▾</span>
          </span>
        </summary>
        <div className="px-4 pb-4 pt-1 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 mt-2">
            {fullList.map((c) => {
              const sourceCount = sourcesRisque.filter(s => s.category === c.id).length;
              const isHidden = hiddenCategories.includes(c.id);
              const canHide = sourceCount === 0;
              const lockedTooltip = t(
                'workshop2.activity1.categoryConfig.lockedTooltip',
                { count: sourceCount }
              );
              return (
                <div
                  key={c.id}
                  className={`flex items-center justify-between gap-2 px-3 py-2 rounded border ${isHidden ? 'border-gray-300 bg-gray-100' : 'border-gray-200 bg-white'}`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xl">{c.icon}</span>
                    <span className="text-sm truncate">{c.name}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${sourceCount === 0 ? 'bg-gray-100 text-gray-500' : 'bg-red-100 text-red-700'}`}>
                      {sourceCount}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleHidden(c.id)}
                    disabled={!canHide}
                    title={canHide ? '' : lockedTooltip}
                    className={`p-1.5 rounded ${isHidden ? 'text-gray-400 hover:text-gray-600' : 'text-indigo-600 hover:text-indigo-800'} ${!canHide ? 'opacity-40 cursor-not-allowed' : 'hover:bg-gray-100'}`}
                    aria-label={isHidden ? t('workshop2.activity1.categoryConfig.show', 'Afficher') : t('workshop2.activity1.categoryConfig.hide', 'Masquer')}
                  >
                    {isHidden ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </details>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {visibleList.map(renderCard)}

        {/* Add button — last grid cell */}
        <motion.button
          type="button"
          onClick={() => openEditor(null)}
          whileHover={{ y: -5 }}
          className="rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-indigo-400 hover:text-indigo-600 transition-colors flex flex-col items-center justify-center min-h-[120px] text-gray-400"
        >
          <Plus size={32} />
          <span className="mt-2 text-sm font-medium">{t('workshop2.activity1.categoryManager.addButton')}</span>
        </motion.button>
      </div>

      <div className="flex justify-between items-center pt-4 border-t border-gray-200">
        <div className="text-gray-600">
          {t('workshop2.activity1.threatCategories.selectedCount', { count: actualSelectedCategories.length, total: visibleList.length })}
        </div>
        {saving && (
          <div className="text-sm text-gray-400">{t('workshop2.activity1.categoryManager.saving', 'Sauvegarde...')}</div>
        )}
      </div>

      {editorOpen && (
        <CategoryEditorModal
          isOpen={editorOpen}
          onClose={() => { setEditorOpen(false); setEditingCategory(null); }}
          onSave={handleSaveCustom}
          initial={editingCategory}
        />
      )}

      {deleteOpen && deletingCategory && (
        <DeleteConfirmationModal
          isOpen={deleteOpen}
          onClose={() => { setDeleteOpen(false); setDeletingCategory(null); }}
          onConfirm={handleConfirmDelete}
          category={deletingCategory}
          affectedSources={affectedSources}
        />
      )}
    </div>
  );
};

export default ThreatCategories;
